// COMMANDES DE TRÉSORERIE — Lot A de l'audit (C01, C02, C03).
//
// Plus aucune publication du registre `oliva.finances` depuis le cache du poste :
// chaque opération est une commande (delta + identifiant stable) appliquée par le
// serveur, dans une transaction, sur le registre courant.
//
// TROIS issues, jamais deux :
//   • confirmée  : le serveur a répondu, les écritures existent ;
//   • refusée    : le serveur a répondu NON (aucune écriture) ;
//   • INDÉTERMINÉE : aucun verdict exploitable (réseau coupé, pod redémarré…).
//     On ne prétend JAMAIS qu'aucune écriture n'a eu lieu, et une opération
//     « introuvable » lors d'une vérification ne prouve pas qu'elle ne sera pas
//     écrite ensuite : l'absence n'est acquise que lorsque le serveur a VERROUILLÉ
//     l'identifiant d'opération (`POST /api/finances/commande/{id}/abandon`).
//     Jusque-là l'opération reste en attente, la caisse reste verrouillée et
//     AUCUNE nouvelle clé n'est créée pour retenter la même saisie.
import { imposerFinances } from './store';
import {
  signalerEchecTresorerie,
  signalerSuccesTresorerie,
  signalerIndeterminees,
  signalerEnvoisEnCours,
  etatTresorerie,
  MESSAGE_BLOCAGE,
} from './tresorerieEtat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EN_ATTENTE = 'oliva.finances.commandes_en_attente';
// Une commande tout juste partie peut encore être en vol (autre onglet) : on ne
// verrouille pas son identifiant avant ce délai.
const DELAI_EN_VOL = 20000;

// Envois réellement en vol DANS CET ONGLET, en mémoire et jamais persistés : un
// rechargement le vide, et un identifiant resté « en_cours » redevient alors une
// vraie inconnue à faire trancher par le serveur.
const enVol = new Set();
const publierEnVol = () => signalerEnvoisEnCours([...enVol]);
// Ne republie QUE le compteur d'envois : surtout ne pas recalculer la liste des
// opérations sans verdict ici, sinon le bandeau rouge clignoterait au retour du
// serveur, avant même que le verdict ait été appliqué.
const finVol = (operationId) => {
  enVol.delete(operationId);
  publierEnVol();
};

const lireAttente = () => {
  try {
    const rows = JSON.parse(localStorage.getItem(EN_ATTENTE) || '[]');
    return Array.isArray(rows) ? rows : [];
  } catch {
    return [];
  }
};

// Une requête ENCORE EN VOL n'est pas un résultat indéterminé : son sort n'est pas
// en jeu, le serveur est simplement en train de répondre. Seules sont « sans verdict »
// les opérations déclarées indéterminées et celles restées « en_cours » alors que
// plus aucune requête ne les porte (onglet rechargé, poste redémarré).
const sansVerdict = (rows) =>
  rows.filter((x) => x.etat === 'indetermine' || (x.etat === 'en_cours' && !enVol.has(x.operation_id)));

const ecrireAttente = (rows) => {
  const gardees = rows; // Ne jamais perdre un identifiant sans verdict.
  localStorage.setItem(EN_ATTENTE, JSON.stringify(gardees));
  // Le blocage de la caisse est commandé par cette liste, pas par la sonde de santé.
  signalerIndeterminees(sansVerdict(gardees));
};

export const commandesIndeterminees = () => sansVerdict(lireAttente());
export const commandesEnAttente = () => lireAttente();

const oublier = (operationId) => ecrireAttente(lireAttente().filter((x) => x.operation_id !== operationId));

const marquerIndeterminee = (operationId, cause) =>
  ecrireAttente(
    lireAttente().map((x) => (x.operation_id === operationId ? { ...x, etat: 'indetermine', cause } : x))
  );

export const nouvelIdOperation = (action) =>
  `OP-${String(action).replace(/[^a-z]/gi, '').slice(0, 10)}-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 8)}`.toUpperCase();

// Un refus MOTIVÉ par le serveur (validation, droit, conflit) ne prouve à lui seul
// RIEN : il peut aussi bien signaler qu'aucune écriture n'a eu lieu qu'un conflit
// d'idempotence sur une opération DÉJÀ ÉCRITE. Il autorise seulement à afficher le
// motif réel une fois que le journal de l'opération a confirmé l'absence d'écriture.
// Les pannes (500, 502, 503, 504) et les réponses sans motif n'autorisent même pas cela.
const REFUS_MOTIVE = new Set([400, 403, 404, 409, 422]);

async function appel(chemin, options) {
  const r = await fetch(`${API}${chemin}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...(options?.headers || {}) },
  });
  let corps;
  try { corps = await r.json(); } catch { throw new Error('Réponse serveur illisible : verdict inconnu.'); }
  if (!r.ok) {
    const motif = typeof corps.detail === 'string' ? corps.detail.trim() : '';
    const e = new Error(motif || `${options?.method || 'GET'} ${chemin} → ${r.status}`);
    e.statut = r.status;
    e.refusMotive = REFUS_MOTIVE.has(r.status) && motif.length > 0;
    throw e;
  }
  return corps;
}

// Résultat serveur appliqué au cache : la copie du serveur fait foi.
const adopter = (reponse) => {
  if (reponse?.data) imposerFinances(reponse.data, reponse.version);
  signalerSuccesTresorerie();
  return reponse;
};

/**
 * Envoie une commande financière.
 * @returns {{error: string|null, indetermine?: boolean, refus?: boolean, operation_id: string|null, ids?: string[], version?: number, alerte?: string|null}}
 */
export async function envoyerCommande(action, corps = {}) {
  // Protection contre le double clic et les écritures concurrentes : une attente
  // normale de réponse n'est PAS un incident, le message le dit sans alarmer.
  if (enVol.size)
    return {
      error:
        'Enregistrement en cours… la base n’a pas encore confirmé l’écriture de caisse ' +
        `(${[...enVol].join(', ')}). Attendez la confirmation : ne cliquez pas une seconde fois.`,
      operation_id: null,
      enCours: true,
    };

  const attente = commandesIndeterminees();
  if (attente.length)
    return {
      error:
        `OPÉRATION PRÉCÉDENTE SANS VERDICT (${attente.map((x) => x.operation_id).join(', ')}) : ` +
        'impossible d’enregistrer une nouvelle écriture de caisse tant que la base n’a pas tranché son sort. ' +
        'Utilisez « Vérifier la base » dans le bandeau rouge en haut de l’écran — ne ressaisissez pas l’opération.',
      operation_id: null,
    };

  const blocage = etatTresorerie();
  if (blocage.bloque)
    return {
      error:
        `${MESSAGE_BLOCAGE} Dernier refus : ${blocage.message || 'cause inconnue'}. ` +
        'Utilisez « Vérifier la base » dans le bandeau rouge en haut de l’écran.',
      operation_id: null,
    };

  const operation_id = corps.operation_id || nouvelIdOperation(action);
  const commande = { ...corps, operation_id, action };
  // L'identifiant est mémorisé AVANT l'envoi : sans cela, une réponse perdue
  // deviendrait une opération orpheline impossible à réconcilier. Il est marqué
  // « en vol » dans le même temps pour ne pas être pris pour une inconnue.
  enVol.add(operation_id);
  publierEnVol();
  ecrireAttente([
    { operation_id, action, resume: corps.resume ?? null, le: new Date().toISOString(), etat: 'en_cours' },
    ...lireAttente().filter((x) => x.operation_id !== operation_id),
  ]);

  try {
    const reponse = await appel('/finances/commande', { method: 'POST', body: JSON.stringify(commande) });
    if (reponse.etat !== 'confirmee' || reponse.operation_id !== operation_id || !reponse.data)
      throw new Error('Confirmation serveur incomplète : verdict inconnu.');
    adopter(reponse);
    oublier(operation_id);
    return { error: null, ...reponse };
  } catch (e) {
    // AUCUN statut HTTP ne vaut verdict à lui seul : un refus peut masquer un conflit
    // d'idempotence portant sur une opération DÉJÀ ÉCRITE, ou une opération déjà
    // traitée. Le journal de l'identifiant d'opération est la seule source de vérité,
    // et il est consulté dans TOUS les cas — jamais de second paiement.
    const verdict = await reconcilier(operation_id);
    if (verdict.confirmee) return { error: null, ...verdict.reponse };
    if (verdict.absente) {
      if (e.refusMotive)
        // Le serveur a motivé son refus ET son journal ne contient rien, identifiant
        // désormais verrouillé : l'absence d'écriture est acquise. Motif réel affiché,
        // aucun verrouillage de la caisse puisqu'il n'y a plus d'incertitude.
        return {
          error:
            `Opération REFUSÉE par la base : ${e.message}. Aucune écriture n’a été faite : ` +
            'ni mouvement de caisse, ni bon soldé, ni stock mouvementé.',
          operation_id,
          refus: true,
        };
      signalerEchecTresorerie(`envoi interrompu (${e.message}) — identifiant verrouillé par la base`);
      return {
        error:
          `Envoi interrompu (${e.message}). Verdict de la base : l’opération ${operation_id} n’y figure pas et ` +
          'son identifiant vient d’être VERROUILLÉ — elle ne pourra plus s’écrire. Contrôlez l’écran avant toute ' +
          'nouvelle saisie.',
        operation_id,
        refus: true,
      };
    }
    return {
      error:
        `RÉSULTAT INDÉTERMINÉ (${verdict.cause || e.message}) : impossible de savoir si la base a enregistré ` +
        `l’opération ${operation_id}. NE RECOMMENCEZ PAS la saisie : utilisez « Vérifier la base » dans le ` +
        'bandeau rouge, l’opération sera retrouvée par son identifiant.',
      operation_id,
      indetermine: true,
    };
  } finally {
    // L'opération cesse d'être « en vol » seulement quand son sort est tranché :
    // l'envoi ET la réconciliation sont couverts par le même message d'attente.
    finVol(operation_id);
  }
}

/**
 * Interroge le serveur sur une opération : seule source de vérité après une panne.
 * Une absence constatée ne suffit pas — elle n'est acquise que lorsque le serveur
 * a verrouillé l'identifiant (plus aucune écriture ne pourra l'utiliser).
 */
export async function reconcilier(operationId) {
  let cause = null;
  try {
    const r = await appel(`/finances/commande/${encodeURIComponent(operationId)}`, { method: 'GET' });
    if (r.trouvee && r.etat === 'confirmee') {
      adopter(r);
      oublier(operationId);
      return { confirmee: true, reponse: r };
    }
    cause = r.trouvee
      ? `opération présente mais non confirmée (${r.etat || 'état inconnu'})`
      : 'opération non retrouvée lors de la dernière vérification';
  } catch (e) {
    cause = `vérification impossible (${String(e.message ?? e)})`;
  }

  try {
    const a = await appel(`/finances/commande/${encodeURIComponent(operationId)}/abandon`, { method: 'POST' });
    if (a.verdict === 'confirmee') {
      adopter(a);
      oublier(operationId);
      return { confirmee: true, reponse: a };
    }
    if (a.verdict === 'absente_definitive') {
      oublier(operationId);
      return { absente: true, cause };
    }
    cause = `${cause} ; la base n’a pas tranché${a.cause ? ` (${a.cause})` : ''}`;
  } catch (e) {
    cause = `${cause} ; verrouillage de l’identifiant impossible (${String(e.message ?? e)})`;
  }

  marquerIndeterminee(operationId, cause);
  return { indetermine: true, cause };
}

// Au chargement (et après « Vérifier la base ») : toute opération restée sans
// verdict est tranchée par le serveur, jamais par une supposition du poste.
export async function reconcilierEnAttente() {
  const rows = lireAttente();
  if (!rows.length) {
    signalerIndeterminees([]);
    return { traitees: 0, confirmees: 0, absentes: 0, indeterminees: 0, en_vol: 0 };
  }
  let confirmees = 0;
  let absentes = 0;
  let indeterminees = 0;
  let enVol = 0;
  for (const row of rows) {
    // Commande peut-être encore en vol depuis un autre onglet : on ne la tranche pas.
    if (row.etat !== 'indetermine' && Date.now() - new Date(row.le || 0).getTime() < DELAI_EN_VOL) {
      enVol += 1;
      continue;
    }
    const v = await reconcilier(row.operation_id);
    if (v.confirmee) confirmees += 1;
    else if (v.absente) absentes += 1;
    else indeterminees += 1;
  }
  return { traitees: rows.length - enVol, confirmees, absentes, indeterminees, en_vol: enVol };
}

// Le blocage survit au rechargement : la liste persistée est republiée dès l'import.
signalerIndeterminees(commandesIndeterminees());
